<template>
    <Modal v-model:open="isOpen" :title="$t('vat-report.remarks.add-remarks')">
        <template #body>
            <div class="flex flex-col gap-2">
                <FormField :label="$t('vat-report.remarks.structured-remarks')">
                    <CheckboxGroup
                        v-model="selectedRemarks"
                        class="w-full"
                        data-cy="structured-remarks-checkbox-Group"
                        :items="remarks"
                        value-key="value"
                    />
                </FormField>
                <FormField
                    :label="$t('vat-report.remarks.text-field')"
                    help="Ved bruk av fritekst vil MVA-meldingen behandles manuelt"
                >
                    <Textarea
                        id="remarksInput"
                        v-model="remarksInput"
                        placeholder="Skriv inn fritekst her..."
                        class="w-full"
                        data-cy="line-remarks-textarea"
                    />
                </FormField>
            </div>
        </template>
        <template #footer>
            <div class="w-full flex flex-row-reverse gap-2">
                <Button
                    data-cy="save-line-remarks-button"
                    @click.stop="handleSave"
                >
                    Lagre
                </Button>
                <Button color="neutral" variant="ghost" @click.stop="handleClose">
                    Lukk
                </Button>
            </div>
        </template>
    </Modal>
</template>

<script setup lang="ts">
import Modal from '@nuxt/ui/components/Modal.vue'
import Button from '@nuxt/ui/components/Button.vue'
import Textarea from '@nuxt/ui/components/Textarea.vue'
import FormField from '@nuxt/ui/components/FormField.vue'
import CheckboxGroup from '@nuxt/ui/components/CheckboxGroup.vue'
import {computed, ref} from 'vue'

const remarks = [
    {id: 'before-vat-input', value: 'Anskaffelser foretatt før mva registrering', label: 'Anskaffelser foretatt før mva registrering'},
    {id: 'credit-note-input', value: 'Kreditnota', label: 'Kreditnota'},
    {id: 'periodization-input', value: 'Periodisering', label: 'Periodisering'},
    {id: 'insurance-settlement-input', value: 'Forsikringsoppgjør', label: 'Forsikringsoppgjør'},
    {id: 'passenger-vehicle-input', value: 'Personkjøretøy', label: 'Personkjøretøy'},
    {id: 'error-accounting-program', value: 'Feil i regnskapsprogram', label: 'Feil i regnskapsprogram'},
    {id: 'wrong-vat-code-used-before', value: 'Feil mva-kode brukt tidligere', label: 'Feil mva-kode brukt tidligere'},
    {id: 'major-acquisitions', value: 'Store anskaffelser', label: 'Store anskaffelser'},
    {id: 'customs-incorrect-org-number', value: 'Tolldeklarasjon på feil organisasjonsnummer', label: 'Tolldeklarasjon på feil organisasjonsnummer'},
]

const selectedRemarks = ref<string[]>([])
const remarksInput = ref<string>('')

type SaveVatLineComment = (comments: string) => void
const defaultSave: SaveVatLineComment = () => console.error('save-vat-line function not defined')

const isOpen = ref(false)
const concatenatedRemarks = computed(() => {
    const remarksString = selectedRemarks.value.join(', ')
    if (!remarksString) {
        return remarksInput.value
    }
    return remarksInput.value ? `${remarksString}, ${remarksInput.value}` : remarksString
})
const saveFunction = ref<SaveVatLineComment>(defaultSave)

const handleOpen = (comments: string, onSave: SaveVatLineComment) => {
    unpack(comments)
    saveFunction.value = onSave
    isOpen.value = true
}

// TODO reconsider life
const unpack = (comments: string) => {
    const cs = comments.split(', ')
    cs.forEach(c => {
        const remark = remarks.find(r => r.value === c)
        if (remark) {
            selectedRemarks.value.push(remark.value)
        } else {
            if (remarksInput.value.length > 0) {
                remarksInput.value += ', '
            }
            remarksInput.value += c
        }
    })
}

defineExpose({handleOpen})

const handleSave = () => {
    saveFunction.value(concatenatedRemarks.value)
    handleClose()
}

const handleClose = () => {
    handleClear()
    isOpen.value = false
}

const handleClear = () => {
    selectedRemarks.value = []
    remarksInput.value = ''
    saveFunction.value = defaultSave
}

</script>

<style scoped>

</style>